import { ChevronDown, ChevronRight, CircleX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { PHOTO_TYPE_EMOJIS } from '@/constants';
import { usePhotos } from '@/hooks/usePhotos';
import { api } from '@/lib/api';
import { Photo } from '@/lib/Photo';
import { PhotoGeo } from '@/types';

import { DateTimeChooser } from './DateTimeChooser';
import { LocationEditor } from './LocationEditor';

export const PhotoForm = ({ photo }: { photo: Photo }) => {
  const navigate = useNavigate();
  const { loadPhotos } = usePhotos();
  const [title, setTitle] = useState(photo.title);
  const [description, setDescription] = useState(photo.description);
  const [type, setType] = useState(photo.type);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(photo.imageUrl || null);
  const [dateTime, setDateTime] = useState<Date>(new Date());
  const [geo, setGeo] = useState<PhotoGeo | null>(photo.geo || null);
  const { pathname } = useLocation();
  const isNewPhoto = pathname === '/photos/upload'; // hacky
  const [isPhotoSectionExpanded, setIsPhotoSectionExpanded] = useState(true);
  const [isLocationSectionExpanded, setIsLocationSectionExpanded] = useState(true);

  const onCloseClick = () => { navigate(-1); };

  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Calculate submit button disabled state - must be reactive to selectedFile and previewUrl changes
  // TEMPORARILY DISABLED: Android Chrome has issues with this reactive state
  // TODO: Fix Android Chrome button enabling issue properly
  const submitDisabled = useMemo(() => {
    // Always enable the button for now (temporary workaround)
    return false;

    // Original logic (disabled until Android Chrome issue is resolved):
    // if (!isNewPhoto) return false;
    // return !selectedFile && !previewUrl && !photo.imageUrl;
  }, [isNewPhoto, selectedFile, previewUrl, photo.imageUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    const confirmed = window.confirm('Are you sure you want to remove this image?');

    if (!confirmed) {
      return;
    }

    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const appendFormData = (formData: FormData, data: any, prefix = '') => {
    if (data === null || data === undefined) return;

    if (typeof data === 'object' && !(data instanceof File)) {
      Object.entries(data).forEach(([key, value]) => {
        const newPrefix = prefix ? `${prefix}[${key}]` : key;
        appendFormData(formData, value, newPrefix);
      });
    } else if (data instanceof File) {
      formData.append(prefix, data);
    } else {
      formData.append(prefix, String(data));
    }
  };

  const handleUpload = async () => {
    if (isNewPhoto && !selectedFile) return;

    // TODO: I don't like this naming, at the very least
    const photoData = {
      form_data: {
        photo: {
          properties: {
            title: title || '',
            description: description || '',
            type: type || '',
            date: dateTime.toISOString(),
            ...(geo && { geo }),
          }
        }
      },
      file: selectedFile,
      // Add remove_image parameter when there was a previous image but it was removed
      ...(isNewPhoto ? {} : { remove_image: !selectedFile && !previewUrl && photo.imageUrl ? true : undefined })
    };

    const formData = new FormData();
    appendFormData(formData, photoData);

    try {
      if (isNewPhoto) {
        const response = await api.post('/photos', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        loadPhotos();
        navigate(`/photos/${response.data.id}`);
      } else {
        await api.put(`/photos/${photo.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        loadPhotos();
        navigate(photo.url());
      }
    } catch (error) {
      console.error('Upload failed:', error);
      // TODO: Show error message to user
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm('Are you sure you want to delete this photo? This action cannot be undone.');

    if (!confirmed) {
      return;
    }

    try {
      await api.post(`/photos/${photo.id}/archive`);
      loadPhotos();
      navigate('/photos');
    } catch (error) {
      console.error('Archive failed:', error);
    }
  };

  const handleGeoChange = useCallback((geo: PhotoGeo) => {
    setGeo(geo);
    // Not right now; reconsidering...
    // submitButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const convertGPStoDecimal = (gps: number[] | undefined, ref: string | undefined): number | null => {
    if (!gps || !ref || !Array.isArray(gps) || gps.length < 3) {
      return null;
    }
    const degrees = gps[0];
    const minutes = gps[1];
    const seconds = gps[2];
    const decimal = degrees + minutes / 60 + seconds / 3600;
    return ref === 'S' || ref === 'W' ? -decimal : decimal;
  };

  const file = fileInputRef.current?.files?.[0];

  useEffect(() => {
    if (file) {
      EXIF.getData(file, function (this: any) {
        const exif = EXIF.getAllTags(this);

        console.log('exif', exif);

        // Check if GPS data exists before processing
        const hasGPS = exif['GPSLatitude'] && exif['GPSLongitude'] &&
                       exif['GPSLatitudeRef'] && exif['GPSLongitudeRef'];

        if (hasGPS) {
          const lat = convertGPStoDecimal(exif['GPSLatitude'] as number[], exif['GPSLatitudeRef'] as string);
          const lng = convertGPStoDecimal(exif['GPSLongitude'] as number[], exif['GPSLongitudeRef'] as string);

          if (lat !== null && lng !== null && isFinite(lat) && isFinite(lng)) {
            console.log('GPS coordinates found - lat:', lat, 'lng:', lng);
            setGeo({
              lat,
              lng,
            });
          } else {
            console.log('GPS data present but could not be converted to valid decimal coordinates (got lat:', lat, 'lng:', lng, ')');
          }
        } else {
          console.log('No GPS coordinates found in image EXIF data');
        }

        // Process date/time if available
        const dateTime = exif['DateTimeDigitized'] || exif['DateTimeOriginal'] || exif['DateTime'] || '';

        if (dateTime) {
          try {
            // parse dateTime as YYYY:MM:DD HH:MM:SS
            const dateTimeParts = dateTime.split(' ');
            const dateParts = dateTimeParts[0].split(':').map(Number);
            const timeParts = dateTimeParts[1].split(':').map(Number);
            const newDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1], timeParts[2]);
            console.log('Date/time found:', newDate);
            setDateTime(newDate);
          } catch (error) {
            console.log('Could not parse date/time from EXIF data:', error);
          }
        } else {
          console.log('No date/time found in image EXIF data');
        }
      });
    }
  }, [file]);

  console.log('datetime', dateTime);

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-white rounded-lg">
      <button
        onClick={onCloseClick}
        className="absolute z-10 flex items-center justify-center w-8 h-8 text-2xl text-white rounded-full cursor-pointer top-2 right-2 hover:opacity-75"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <CircleX className="w-8 h-8" />
      </button>

      <div className="flex-1 p-4 space-y-8 sm:p-8">
        <div className="space-y-6">
          <div className="space-y-3">
            <div
              className="flex items-center w-full cursor-pointer"
              onClick={() => setIsPhotoSectionExpanded(!isPhotoSectionExpanded)}
            >
              {isPhotoSectionExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
              <span className="ml-1 text-lg font-medium text-gray-700">Photo</span>
            </div>
            {isPhotoSectionExpanded && (
              <div>
                {!previewUrl ? (
                  <div className="flex justify-center px-6 pt-8 pb-8 mt-2 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-2 text-center">
                      <svg
                        className="w-12 h-12 mx-auto text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative font-medium text-blue-600 bg-white rounded-md cursor-pointer hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                        >
                          <span>Upload a file</span>
                          <input
                            ref={fileInputRef}
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            onChange={handleFileSelect}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-auto mx-auto rounded-lg max-h-96"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="mt-4 text-sm text-red-600 cursor-pointer hover:text-red-500"
                    >
                      Remove image
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>


          <label className="block">
            <span className="text-lg font-medium text-gray-700">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
              placeholder="Enter photo title"
            />
          </label>

          <label className="block">
            <span className="text-lg font-medium text-gray-700">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
              rows={4}
              placeholder="Enter photo description"
            />
          </label>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-lg font-medium text-gray-700">Type:</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="block w-auto max-w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2.5"
              >
                {PHOTO_TYPE_EMOJIS.map((emoji) => (
                  <option key={emoji} value={emoji}>{emoji}</option>
                ))}
              </select>
            </div>
            <DateTimeChooser value={dateTime} onChange={setDateTime} />
          </div>

          <div className="space-y-3">
            <div
              className="flex items-baseline w-full cursor-pointer"
              onClick={() => setIsLocationSectionExpanded(!isLocationSectionExpanded)}
            >
              {isLocationSectionExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
              <span className="ml-1 text-lg font-medium text-gray-700">Location</span>
            </div>
            {isLocationSectionExpanded && (
              <div className="mt-2">
                <LocationEditor
                  geo={geo}
                  onGeoChange={handleGeoChange}
                />
              </div>
            )}
          </div>
        </div>

        <div className={`flex ${isNewPhoto ? 'justify-end' : 'justify-between'} pt-4`}>
          {!isNewPhoto && (
            <button
              onClick={handleDelete}
              className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer"
            >
              Delete
            </button>
          )}
          <div className="flex space-x-4">
            <button
              onClick={onCloseClick}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              ref={submitButtonRef}
              onClick={handleUpload}
              disabled={submitDisabled}
              className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isNewPhoto ? 'Upload' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
